import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Crown, Flag, Users, ShieldCheck, HandHeart, Trophy, Handshake, Megaphone, Calendar as CalIcon, Rocket, ClipboardList } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { LOGO_URL } from "@/lib/api";
import api from "@/lib/api";

// Official SCU promotional artwork
const SCU_HEADER = "https://customer-assets-7cd3h4nn.emergentagent.net/job_parish-united/artifacts/n65p8pb0_header%201.png";
const SCU_FOOTBALL_PHOTO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_parish-united/artifacts/1bvle8sd_football%20photo.png";
const SCU_CHEER_PHOTO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_parish-united/artifacts/2f8qkimi_cheere%20photo.png";

const values = [
  { icon: ShieldCheck, title: "Discipline", copy: "Athletic and academic accountability every day." },
  { icon: Users, title: "Teamwork", copy: "United across the parish, one team, one heartbeat." },
  { icon: Trophy, title: "Character", copy: "Building young men and women of integrity." },
  { icon: HandHeart, title: "Community", copy: "Families, coaches, sponsors — all in." },
];

const roadmap = [
  { icon: Flag, title: "Organization Launch", period: "2026", body: "Building the leadership, structure, identity and foundation of St. Charles United." },
  { icon: ClipboardList, title: "Pre-Registration", period: "In Progress", body: "Launch an early pre-registration period to gauge community interest and estimate the number of football players and cheerleaders interested in joining SCU for our inaugural 2027 season. This early participation data will help us plan appropriately and provide potential sponsors and community partners with measurable evidence of SCU's expected reach, participation and community impact." },
  { icon: Handshake, title: "Community & Sponsor Partnerships", period: "In Progress", body: "Developing relationships with businesses, community leaders and supporters throughout St. Charles Parish." },
  { icon: Users, title: "Coaching Staff Announcements", period: "Coming Soon", body: "Introducing the coaches and volunteers who will lead SCU Football and Cheer." },
  { icon: ClipboardList, title: "Camps & Clinics", period: "Dates TBA", body: "Opportunities for athletes to meet our coaches, develop their skills and experience SCU." },
  { icon: Megaphone, title: "Tryouts & Evaluations", period: "Dates TBA", body: "Football and Cheer tryout/evaluation information will be announced as the inaugural season approaches." },
  { icon: CalIcon, title: "Registration", period: "2027", body: "Online registration for the inaugural season." },
  { icon: Rocket, title: "Inaugural Season", period: "2027", body: "The St. Charles United Utes officially begin competition." },
];

export default function Home() {  const [announcements, setAnn] = useState([]);
  const [events, setEvents] = useState([]);
  const [sponsors, setSponsors] = useState([]);

  useEffect(() => {
    api.get("/announcements").then(r => setAnn(r.data.slice(0, 3))).catch(() => {});
    api.get("/events").then(r => setEvents(r.data.slice(0, 4))).catch(() => {});
    api.get("/sponsors").then(r => setSponsors(r.data)).catch(() => {});
  }, []);

  // Fill sponsor grid to 6 with "YOUR BUSINESS HERE" placeholders
  const sponsorSlots = [
    ...sponsors,
    ...Array.from({ length: Math.max(0, 6 - sponsors.length) }, (_, i) => ({ id: `open-${i}`, name: "YOUR BUSINESS HERE", level: "Open", open: true })),
  ].slice(0, 6);

  return (
    <div data-testid="home-page">
      {/* Announcement banner */}
      <div data-testid="announcement-banner" className="bg-scu-purple text-white text-center text-xs sm:text-sm font-bold uppercase tracking-[0.25em] py-2">
        Coming 2027 — St. Charles United Utes
      </div>

      {/* Founding Partner Strip — renders permanently once first tier-1 sponsor commits */}
      <FoundingPartnerStrip sponsors={sponsors} />

      {/* HERO */}
      <section className="relative overflow-hidden bg-scu-obsidian text-white">
        <img src={SCU_HEADER} alt="SCU Utes header" className="absolute inset-0 h-full w-full object-cover opacity-55" style={{ objectPosition: "center 35%" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-scu-obsidian/70 via-scu-obsidian/50 to-scu-obsidian" />
        <div className="scanlines absolute inset-0 opacity-30" />

        <div className="relative mx-auto max-w-[1400px] px-4 lg:px-8 py-24 md:py-36 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="inline-flex items-center gap-2 border border-white/20 bg-white/5 backdrop-blur-md px-3 py-1 rounded-md">
              <span className="h-2 w-2 rounded-full bg-scu-electric animate-pulse" />
              <span className="text-[10px] uppercase tracking-[0.35em] text-white/80">St. Charles Parish · Louisiana</span>
            </div>
            <h1 className="mt-6 font-bebas uppercase tracking-wider leading-[0.9] text-6xl sm:text-7xl md:text-8xl lg:text-9xl">
              St. Charles<br />
              <span className="text-scu-electric">United</span>
            </h1>
            <div className="mt-4 h-1 w-24 bg-scu-electric" />
            <p className="mt-8 font-bebas text-3xl sm:text-4xl tracking-widest text-white/95">
              United Together. Empowering St. Charles.
            </p>
            <p className="mt-3 text-xs sm:text-sm uppercase tracking-[0.35em] text-scu-electric font-bold">
              U · T · E · S
            </p>
            <p className="mt-6 max-w-xl text-lg text-white/70">
              Developing athletes. Building character. Uniting St. Charles Parish.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Button data-testid="hero-join-btn" asChild className="bg-scu-purple hover:bg-scu-electric text-white font-bold uppercase tracking-widest h-12 px-6 rounded-md">
                <Link to="/football">Explore Football <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <Button data-testid="hero-cheer-btn" asChild variant="outline" className="border-white/30 bg-transparent text-white hover:bg-white hover:text-scu-obsidian font-bold uppercase tracking-widest h-12 px-6 rounded-md">
                <Link to="/cheer">Explore Cheer</Link>
              </Button>
              <Button data-testid="hero-sponsor-btn" asChild variant="ghost" className="text-white hover:bg-white/10 hover:text-white font-bold uppercase tracking-widest h-12 px-6 rounded-md">
                <Link to="/sponsors">Become a Sponsor <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>
          </div>
          <div className="md:col-span-4 hidden md:flex justify-center">
            <img src={LOGO_URL} alt="SCU Utes Logo" className="w-full max-w-[360px] drop-shadow-[0_0_60px_rgba(123,67,250,0.4)]" style={{ mixBlendMode: "screen" }} />
          </div>
        </div>
        <div className="stripe-divider" />
      </section>

      {/* WHO WE ARE */}
      <section className="mx-auto max-w-[1400px] px-4 lg:px-8 py-24">
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-scu-purple">Who We Are</div>
            <h2 className="mt-4 font-bebas text-5xl md:text-6xl uppercase tracking-wide text-scu-obsidian leading-none">
              A Program Built<br />For The Whole Parish
            </h2>
          </div>
          <div className="md:col-span-7">
            <p className="text-lg leading-relaxed text-scu-smoke">
              St. Charles United is being developed to provide young athletes throughout St. Charles Parish with a
              quality competitive sports program centered around discipline, teamwork, character, accountability,
              academic responsibility and athletic development.
            </p>
            <p className="mt-4 text-lg leading-relaxed text-scu-smoke">
              SCU represents athletes and families from across St. Charles Parish — not one neighborhood and not one side of the river.
              <span className="block mt-2 font-bold text-scu-purple">United Together. Empowering St. Charles.</span>
            </p>
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
              {values.map((v) => (
                <div key={v.title} className="border border-scu-smoke/20 bg-white p-5 rounded-md hover:-translate-y-1 transition-transform duration-200">
                  <v.icon className="h-6 w-6 text-scu-purple" />
                  <div className="mt-3 font-bebas text-xl tracking-wider text-scu-obsidian">{v.title}</div>
                  <div className="text-sm text-scu-smoke mt-1">{v.copy}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROGRAMS */}
      <section className="bg-scu-obsidian text-white py-24">
        <div className="mx-auto max-w-[1400px] px-4 lg:px-8">
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-scu-electric">Programs</div>
          <h2 className="mt-3 font-bebas text-5xl md:text-6xl uppercase tracking-wide">Three Programs. One Mission.</h2>

          <div className="mt-12 grid md:grid-cols-3 gap-6">
            <Link to="/football" data-testid="home-football-card" className="group relative overflow-hidden border border-white/10 rounded-md h-96">
              <img src={SCU_FOOTBALL_PHOTO} alt="Utes Football" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" style={{ objectPosition: "center 35%" }} />
              <div className="absolute inset-0 bg-gradient-to-t from-scu-obsidian via-scu-obsidian/40 to-transparent" />
              <div className="absolute bottom-0 p-8">
                <div className="text-xs uppercase tracking-[0.3em] text-scu-electric">Football</div>
                <div className="mt-2 font-bebas text-5xl tracking-wide">Utes Football</div>
                <div className="mt-2 text-white/70 max-w-md">Multiple age divisions. Elite coaching. Player development that prepares athletes for the next level.</div>
                <div className="mt-4 inline-flex items-center gap-2 text-scu-electric font-bold uppercase tracking-widest text-xs">Explore Football <ArrowRight className="h-4 w-4" /></div>
              </div>
            </Link>
            <Link to="/cheer" data-testid="home-cheer-card" className="group relative overflow-hidden border border-white/10 rounded-md h-96">
              <img src={SCU_CHEER_PHOTO} alt="Utes Cheer" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" style={{ objectPosition: "center 30%" }} />
              <div className="absolute inset-0 bg-gradient-to-t from-scu-obsidian via-scu-obsidian/40 to-transparent" />
              <div className="absolute bottom-0 p-8">
                <div className="text-xs uppercase tracking-[0.3em] text-scu-electric">Cheer</div>
                <div className="mt-2 font-bebas text-5xl tracking-wide">Utes Cheer</div>
                <div className="mt-2 text-white/70 max-w-md">Skilled coaching, performance and community. A cheer program that matches the intensity of our football program.</div>
                <div className="mt-4 inline-flex items-center gap-2 text-scu-electric font-bold uppercase tracking-widest text-xs">Explore Cheer <ArrowRight className="h-4 w-4" /></div>
              </div>
            </Link>
            <Link to="/7v7" data-testid="home-7v7-card" className="group relative overflow-hidden border border-white/10 rounded-md h-96 bg-scu-obsidian">
              <img src="https://customer-assets-7cd3h4nn.emergentagent.net/job_parish-united/artifacts/sxtrx0tt_7V7.png" alt="SCU 7v7" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" style={{ objectPosition: "center 15%" }} />
              <div className="absolute inset-0 bg-gradient-to-t from-scu-obsidian via-scu-obsidian/40 to-transparent" />
              <div className="absolute bottom-0 p-8">
                <div className="text-xs uppercase tracking-[0.3em] text-scu-electric">7v7 · Ages 8-12</div>
                <div className="mt-2 font-bebas text-5xl tracking-wide">SCU 7v7</div>
                <div className="mt-2 text-white/80 max-w-md">Speed. Skill. Competition. Development. More reps, sharper skills — build the complete football player.</div>
                <div className="mt-4 inline-flex items-center gap-2 text-scu-electric font-bold uppercase tracking-widest text-xs">Explore 7v7 <ArrowRight className="h-4 w-4" /></div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* WHY SCU */}
      <section className="mx-auto max-w-[1400px] px-4 lg:px-8 py-24">
        <div className="grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-6 relative overflow-hidden rounded-md h-[520px]">
            <img src={SCU_FOOTBALL_PHOTO} alt="SCU Utes football team" className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: "center 40%" }} />
            <div className="absolute -bottom-6 -right-6 hidden md:block bg-scu-purple text-white p-6 rounded-md w-64 scu-purple-glow z-10">
              <div className="font-bebas text-4xl tracking-wide leading-none">2027</div>
              <div className="text-xs uppercase tracking-[0.3em] mt-1 text-white/80">Inaugural Season</div>
            </div>
          </div>
          <div className="md:col-span-6">
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-scu-purple">Why SCU?</div>
            <h2 className="mt-3 font-bebas text-5xl md:text-6xl uppercase tracking-wide text-scu-obsidian leading-none">
              A Serious<br />Youth Sports<br />Organization
            </h2>
            <ul className="mt-8 space-y-4 text-scu-smoke text-lg">
              <li className="flex gap-3"><span className="text-scu-purple font-bold">01</span> Competitive travel-league quality across every age division.</li>
              <li className="flex gap-3"><span className="text-scu-purple font-bold">02</span> Coaches held to a code of conduct and player development standard.</li>
              <li className="flex gap-3"><span className="text-scu-purple font-bold">03</span> Academic responsibility as a requirement, not a suggestion.</li>
              <li className="flex gap-3"><span className="text-scu-purple font-bold">04</span> Parents kept informed with schedules, forms and communication in one place.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ROAD TO 2027 */}
      <section className="bg-scu-obsidian text-white py-24 relative overflow-hidden">
        <div className="scanlines absolute inset-0 opacity-30" />
        <div className="mx-auto max-w-[1400px] px-4 lg:px-8 relative">
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-scu-electric">The Road to 2027</div>
          <h2 className="mt-3 font-bebas text-5xl md:text-6xl uppercase tracking-wide">Building St. Charles United<br />From The Ground Up</h2>
          <div className="mt-12 relative">
            {/* vertical spine */}
            <div className="absolute left-4 md:left-1/2 top-2 bottom-2 w-px bg-white/15 md:-translate-x-px" />
            <div className="space-y-8">
              {roadmap.map((r, i) => {
                const flip = i % 2 === 1;
                return (
                  <div key={r.title} className="relative md:grid md:grid-cols-2 md:gap-10 items-center">
                    <div className={`hidden md:block ${flip ? 'md:col-start-2' : ''}`}>&nbsp;</div>
                    <div className={`pl-12 md:pl-0 ${flip ? 'md:col-start-1 md:pr-10 md:text-right' : 'md:col-start-2 md:pl-10'}`}>
                      <div className="border border-white/10 bg-white/5 backdrop-blur-md rounded-md p-6 hover:border-scu-electric transition-colors">
                        <div className="flex items-center gap-3 md:justify-start" style={{ justifyContent: flip ? 'flex-end' : 'flex-start' }}>
                          <r.icon className="h-5 w-5 text-scu-electric" />
                          <div className="text-[10px] uppercase tracking-[0.35em] text-scu-electric font-bold">{r.period}</div>
                        </div>
                        <div className="mt-2 font-bebas text-3xl tracking-wide">{r.title}</div>
                        <div className="mt-2 text-white/70">{r.body}</div>
                      </div>
                    </div>
                    <div className="absolute left-0 md:left-1/2 top-6 md:-translate-x-1/2 flex items-center justify-center h-9 w-9 rounded-full bg-scu-purple border-2 border-scu-electric">
                      <span className="text-[10px] font-bold text-white">{String(i + 1).padStart(2, '0')}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* EVENTS & ANNOUNCEMENTS */}
      <section className="bg-scu-off border-y border-scu-smoke/15 py-20">
        <div className="mx-auto max-w-[1400px] px-4 lg:px-8 grid md:grid-cols-2 gap-10">
          <div>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.3em] text-scu-purple">
              <CalIcon className="h-4 w-4" /> Upcoming
            </div>
            <h3 className="mt-3 font-bebas text-4xl md:text-5xl tracking-wide text-scu-obsidian">Events</h3>
            {events.length === 0 ? (
              <div className="mt-6 p-6 border border-scu-smoke/15 rounded-md bg-white text-scu-smoke">
                SCU camps, clinics, tryouts, community events and inaugural-season activities will be announced as we continue building toward 2027.
              </div>
            ) : (
              <div className="mt-6 divide-y divide-scu-smoke/15 border border-scu-smoke/15 rounded-md bg-white">
                {events.map((e) => (
                  <div key={e.id} className="p-5 flex gap-4 items-center hover:bg-scu-off transition-colors">
                    <div className="flex flex-col items-center justify-center bg-scu-obsidian text-white w-16 h-16 rounded-md">
                      <div className="text-[10px] uppercase tracking-widest text-scu-electric">{new Date(e.date).toLocaleString('default', { month: 'short' })}</div>
                      <div className="font-bebas text-2xl">{new Date(e.date).getDate()}</div>
                    </div>
                    <div className="flex-1">
                      <div className="font-bold text-scu-obsidian">{e.title}</div>
                      <div className="text-sm text-scu-smoke">{e.time} · {e.location}</div>
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-scu-purple font-bold">{e.event_type}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.3em] text-scu-purple">
              <Megaphone className="h-4 w-4" /> Latest
            </div>
            <h3 className="mt-3 font-bebas text-4xl md:text-5xl tracking-wide text-scu-obsidian">Announcements</h3>
            {announcements.length === 0 ? (
              <div className="mt-6 p-6 border border-scu-smoke/15 rounded-md bg-white text-scu-smoke">
                Coaching announcements, sponsor announcements, football updates, cheer updates, registration information, tryouts, camps, clinics and community events will be posted here as we approach the inaugural 2027 season.
              </div>
            ) : (
              <div className="mt-6 space-y-4">
                {announcements.map((a) => (
                  <Card key={a.id} className="border-scu-smoke/15 hover:border-scu-purple transition-colors rounded-md">
                    <div className="p-5">
                      <div className="text-[10px] uppercase tracking-widest text-scu-purple font-bold">{a.category}</div>
                      <div className="mt-1 font-bold text-scu-obsidian">{a.title}</div>
                      <div className="mt-1 text-sm text-scu-smoke">{a.body}</div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
            {announcements.length > 0 && (
              <Link data-testid="home-view-news" to="/news" className="inline-flex items-center gap-2 mt-4 text-scu-purple hover:text-scu-electric font-bold uppercase tracking-widest text-xs">
                All News <ArrowRight className="h-4 w-4" />
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* SPONSORS BAR */}
      <section className="py-16 mx-auto max-w-[1400px] px-4 lg:px-8">
        <div className="text-xs font-bold uppercase tracking-[0.3em] text-scu-purple text-center">Our Sponsors</div>
        <h3 className="text-center mt-3 font-bebas text-4xl md:text-5xl tracking-wide text-scu-obsidian">Powered by Community</h3>
        <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {sponsorSlots.map((s) => (
            <div key={s.id} className={`border rounded-md h-44 flex flex-col items-center justify-center bg-white transition-colors overflow-hidden p-3 ${s.open ? 'border-dashed border-scu-smoke/30 text-scu-smoke/70' : 'border-scu-smoke/20 hover:border-scu-purple'}`}>
              {s.logo_url ? (
                <>
                  <img src={s.logo_url} alt={s.name} className="max-h-32 max-w-[95%] object-contain" />
                  <div className="mt-2 text-sm font-bold text-scu-obsidian text-center leading-tight">{s.name}</div>
                </>
              ) : (
                <div className="text-center px-2">
                  <div className={`text-[10px] uppercase tracking-widest font-bold ${s.open ? 'text-scu-smoke/60' : 'text-scu-purple'}`}>{s.level}</div>
                  <div className={`text-sm font-bold ${s.open ? 'text-scu-smoke/70' : 'text-scu-obsidian'}`}>{s.name}</div>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="text-center mt-8">
          <Button data-testid="home-become-sponsor" asChild className="bg-scu-purple hover:bg-scu-electric text-white font-bold uppercase tracking-widest">
            <Link to="/sponsors">Become a Sponsor</Link>
          </Button>
        </div>
      </section>

      {/* REGISTRATION CTA */}
      <section className="bg-scu-purple text-white relative overflow-hidden">
        <div className="scanlines absolute inset-0 opacity-30" />
        <div className="mx-auto max-w-[1400px] px-4 lg:px-8 py-20 relative flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-white/70">Register for the 2027 season</div>
            <h3 className="mt-2 font-bebas text-5xl md:text-6xl tracking-wide">Ready to Join The Utes?</h3>
            <p className="mt-2 text-white/85 max-w-lg">Complete the SCU 2027 Registration online — sign every waiver electronically and secure your athlete's spot.</p>
          </div>
          <Button data-testid="cta-register-btn" asChild className="bg-white text-scu-purple hover:bg-scu-obsidian hover:text-white font-bold uppercase tracking-widest h-14 px-8 rounded-md text-base">
            <Link to="/registration">Start Registration <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
        </div>
      </section>
    </div>
  );
}

// Permanent Founding Partner strip — auto-renders once the first tier-1 sponsor commits.
// Stays hidden (returns null) until at least one sponsor with level === "Founding Partner" exists.
function FoundingPartnerStrip({ sponsors }) {
  const founders = (sponsors || []).filter((s) => (s.level || "").toLowerCase() === "founding partner");
  if (founders.length === 0) return null;
  return (
    <section
      data-testid="founding-partner-strip"
      className="relative bg-scu-obsidian text-white border-b border-scu-electric/25 overflow-hidden"
    >
      <div className="scanlines absolute inset-0 opacity-20 pointer-events-none" />
      <div className="relative mx-auto max-w-[1400px] px-4 lg:px-8 py-4 md:py-5 flex flex-col md:flex-row items-center gap-4 md:gap-8">
        {/* Label */}
        <div className="flex items-center gap-2 shrink-0">
          <Crown className="h-4 w-4 text-scu-electric" />
          <span className="text-[10px] md:text-xs uppercase tracking-[0.35em] font-bold text-scu-electric">
            {founders.length === 1 ? "Founding Partner" : "Founding Partners"}
          </span>
        </div>
        {/* Divider (desktop) */}
        <div className="hidden md:block h-6 w-px bg-white/15 shrink-0" />
        {/* Logo strip — clickable to /sponsors, keeps aspect ratio via object-contain */}
        <Link
          to="/sponsors"
          data-testid="founding-partner-link"
          className="group flex-1 w-full flex flex-wrap items-center justify-center md:justify-start gap-x-8 gap-y-3"
        >
          {founders.map((s) => (
            <div
              key={s.id}
              data-testid={`founding-${s.id}`}
              className="flex items-center gap-3 opacity-95 group-hover:opacity-100 transition-opacity"
              title={s.name}
            >
              {s.logo_url ? (
                <img
                  src={s.logo_url}
                  alt={s.name}
                  className="h-9 md:h-10 w-auto max-w-[160px] object-contain"
                />
              ) : null}
              <span className="font-bebas text-xl md:text-2xl tracking-wide text-white leading-none">
                {s.name}
              </span>
            </div>
          ))}
        </Link>
      </div>
      <div className="h-[2px] w-full bg-gradient-to-r from-scu-electric via-scu-purple to-scu-electric" />
    </section>
  );
}
